import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.object.entries.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/web.dom-collections.for-each.js";
import { defineComponent as _defineComponent } from 'vue';
import Document from '@tiptap/extension-document';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-2';
import tippy from 'tippy.js';
import HardBreak from '@tiptap/extension-hard-break';
import MentionList from './tiptapMentionList.vue';
import LIST_TABLE_MODELS_QUERY from '~/graphql/queries/ListTableModels.gql';
import { TemplateEngineContext, useValidateTemplateTextQuery } from '~/types/types';
import { renderMarkdown } from '~/functions/markdown';
import { replaceBraces } from '~/functions/helpers';
export default /*#__PURE__*/_defineComponent({
  __name: 'tiptapTemplateEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    isPrompt: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    context: {
      type: String,
      required: false,
      default: function _default() {
        return TemplateEngineContext.Fund;
      }
    }
  },
  emits: ['input', 'blur', 'toggle-disable-save'],
  setup: function setup(__props, _ref) {
    var _nuxt2Context$app,
      _nuxt2Context$app$apo,
      _props$value,
      _this = this;
    var emit = _ref.emit;
    var props = __props;
    var editor = ref(null);
    var limit = ref(200);
    watch(function () {
      return props.value;
    }, function () {
      if (!editor.value) {
        return;
      }
      var html = (props.value || '').replace(/\n/g, '<br>');
      editor.value.commands.setContent(html, false, {
        preserveWhitespace: 'full'
      });
    });
    watch(function () {
      return props.disabled;
    }, function (newVal) {
      if (editor.value) {
        editor.value.setEditable(!newVal);
      }
    });
    var supportedObjectsByContext = _defineProperty(_defineProperty(_defineProperty({}, TemplateEngineContext.Fund, {
      // aligned with the variables passed into jinja_template.render in the API
      fund: {
        type: 'FUND OBJECT (fund)',
        description: 'Fund object'
      },
      social_metrics: {
        type: 'LIST OF METRIC OBJECTS (metric[])',
        description: 'List of additional social metrics considered during PAIs'
      },
      environmental_metrics: {
        type: 'LIST OF METRIC OBJECTS (metric[])',
        description: 'List of additional environmental metrics considered during PAIs'
      },
      sustainable_objectives: {
        type: 'LIST OF SFDR STRATEGY OBJECTS (sfdrstrategy[])',
        description: 'List of sustainable objectives excluding ES characteristic'
      },
      es_characteristic: {
        type: 'SFDR STRATEGY OBJECT (sfdrstrategy)',
        description: 'ES characteristic object'
      },
      account: {
        type: 'ACCOUNT OBJECT (account)',
        description: 'The account object that owns the fund'
      },
      ventures_with_sustainable_objective: {
        type: 'LIST OF ACCOUNT OBJECTS (account[])',
        description: 'List of venture accounts that are in the fund AND a sustainable objective'
      },
      sustainable_investments_by_value_and_objective: {
        type: 'STRING',
        description: "Special string. See the article 9 question: 'The company names, investment amount and percentage of total investment made...'"
      }
    }), TemplateEngineContext.ImpactSolution, {
      // aligned with ImpactSolutionCache variables passed into jinja_template.render
      impact_solution: {
        type: 'IMPACT SOLUTION OBJECT (impactsolution)',
        description: 'Impact solution object'
      },
      impact: {
        type: 'IMPACT OBJECT (impact)',
        description: 'The impact that this solution belongs to'
      },
      concept: {
        type: 'CONCEPT OBJECT (concept)',
        description: 'Concept object associated with the impact solution'
      },
      specific_reduction_per_unit: {
        type: 'NUMBER',
        description: 'Specific GHG reduction per unit (only available if GHG saving potential exists)'
      },
      specific_reduction_as_percentage: {
        type: 'NUMBER',
        description: 'Specific GHG reduction as percentage (only available if GHG saving potential exists)'
      },
      emission_sources: {
        type: 'LIST OF EMISSION SOURCE OBJECTS (emissionsource[])',
        description: 'List of emission sources (only available if GHG saving potential exists)'
      }
    }), TemplateEngineContext.Impact, {
      // aligned with ImpactCache variables passed into jinja_template.render
      impact: {
        type: 'IMPACT OBJECT (impact)',
        description: 'Impact object'
      },
      impact_solutions: {
        type: 'LIST OF IMPACT SOLUTION OBJECTS (impactsolution[])',
        description: 'List of impact solutions belonging to this impact'
      },
      basic_info_for_company: {
        type: 'COMPANY INFO OBJECT (companyinfo)',
        description: 'Basic company information object. Available fields: venture_id (str), venture_name (str), website (str), questionnaire_id (int), pitch_line (str), legal_entity (str), founders (str[]), headquarter (str), country (str), founding_year (str), employee_headcount (str), turnover (str), funding_stage (str), business_model (str[]), income_stream (str[]), products (str[]), industries (str[]), primaryIndustry (str), customer_group (str[]), customer_group_options (str), commercial_register_number (str), postcode (str), company_valuation (str)'
      }
    });
    var supportedObjects = computed(function () {
      return supportedObjectsByContext[props.context] || supportedObjectsByContext[TemplateEngineContext.Fund];
    });
    var tooltip = computed(function () {
      var tooltipText = '<strong>To insert a line break, press Shift + Enter on Windows and Cmd + Enter on Mac.</strong>';
      tooltipText += '<br><br>The following objects are supported. Take note of the type and description of each object:<br><br>';
      tooltipText += '<ul>';
      Object.entries(supportedObjects.value).forEach(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
          key = _ref3[0],
          obj = _ref3[1];
        tooltipText += "<li>".concat(key, "<ul><li>Type: ").concat(obj.type, "</li><li>Description: ").concat(obj.description, "</li></ul></li>");
      });
      tooltipText += '</ul>';
      tooltipText += '<br>To use an objects, type "{{" to access the supported fields.';
      tooltipText += '<br><br>For LISTS, access the nth element:';

      // Use context-appropriate example
      if (props.context === TemplateEngineContext.Fund) {
        tooltipText += ' (eg: "{{ social_metrics[0] }}")';
      } else if (props.context === TemplateEngineContext.ImpactSolution) {
        tooltipText += ' (eg: "{{ emission_sources[0] }}")';
      } else if (props.context === TemplateEngineContext.Impact) {
        tooltipText += ' (eg: "{{ impact_solutions[0] }}")';
      }
      tooltipText += '<br><br>To loop over the list, see the following example:';
      tooltipText += '<br><br><pre>';

      // Use context-appropriate example
      if (props.context === TemplateEngineContext.Fund) {
        tooltipText += '{% for sustainable_objective in sustainable_objectives -%}\n';
        tooltipText += "Regarding '{{ sustainable_objective.concept.label }}':\n";
        tooltipText += '{% endfor %}';
      } else if (props.context === TemplateEngineContext.ImpactSolution) {
        tooltipText += '{% for emission_source in emission_sources -%}\n';
        tooltipText += "Emission source: {{ emission_source.baseline_ghg_factor }}\n";
        tooltipText += '{% endfor %}';
      } else if (props.context === TemplateEngineContext.Impact) {
        tooltipText += '{% for solution in impact_solutions -%}\n';
        tooltipText += "Solution: {{ solution.label }}\n";
        tooltipText += '{% endfor %}';
      }
      tooltipText += '</pre>';
      tooltipText += '<br><br>Similarly, for if statements:';
      tooltipText += '<br><br><pre>';

      // Use context-appropriate example
      if (props.context === TemplateEngineContext.Fund) {
        tooltipText += '{% if fund.is_reporting_pais -%}\n';
        tooltipText += 'The fund reports pais\n';
        tooltipText += '{% endif %}';
      } else if (props.context === TemplateEngineContext.ImpactSolution) {
        tooltipText += '{% if impact_solution.ghg_saving_potential -%}\n';
        tooltipText += 'GHG saving potential exists\n';
        tooltipText += '{% endif %}';
      } else if (props.context === TemplateEngineContext.Impact) {
        tooltipText += '{% if impact_solutions -%}\n';
        tooltipText += 'Impact has solutions\n';
        tooltipText += '{% endif %}';
      }
      tooltipText += '</pre>';
      tooltipText += '<br><br>For more information, see the <a href="https://ttl255.com/jinja2-tutorial-part-2-loops-and-conditionals/" target="_blank">Jinja documentation</a>';
      return tooltipText;
    });
    var _useNuxtApp = useNuxtApp(),
      nuxt2Context = _useNuxtApp.nuxt2Context;
    var apolloClient = nuxt2Context === null || nuxt2Context === void 0 ? void 0 : (_nuxt2Context$app = nuxt2Context.app) === null || _nuxt2Context$app === void 0 ? void 0 : (_nuxt2Context$app$apo = _nuxt2Context$app.apolloProvider) === null || _nuxt2Context$app$apo === void 0 ? void 0 : _nuxt2Context$app$apo.defaultClient;
    var _useValidateTemplateT = useValidateTemplateTextQuery({
        templateText: (_props$value = props === null || props === void 0 ? void 0 : props.value) !== null && _props$value !== void 0 ? _props$value : '',
        context: props.context
      }),
      validateResult = _useValidateTemplateT.result,
      refetchValidation = _useValidateTemplateT.refetch;
    var validatedText = computed(function () {
      var _validateResult$value;
      return (_validateResult$value = validateResult.value) === null || _validateResult$value === void 0 ? void 0 : _validateResult$value.validateTemplateText;
    });
    var validatedProcessedText = computed(function () {
      var vt = validatedText.value;
      return vt && vt.processedText != null ? vt.processedText : undefined;
    });
    var validatedError = computed(function () {
      var vt = validatedText.value;
      return vt && vt.error ? vt.error : '';
    });
    var localQuery = ref('');
    var showItems = ref(false);
    var validating = ref(false);
    var currentText = ref('');
    var tableNames = [];
    if (props.context == TemplateEngineContext.ImpactSolution) {
      tableNames = ['impact_solution', 'emission_source', 'impact', 'concept'];
    } else if (props.context == TemplateEngineContext.Fund) {
      tableNames = ['fund', 'sfdrstrategy', 'account', 'metric', 'concept'];
    } else {
      tableNames = ['impact', 'impactgoal', 'account'];
    }
    var mentionItems = function mentionItems(_ref4) {
      var q = _ref4.query;
      return apolloClient.query({
        query: LIST_TABLE_MODELS_QUERY,
        variables: {
          tableNames: tableNames,
          query: q
        }
      }).then(function (_ref5) {
        var _data$listTableModels;
        var data = _ref5.data;
        return data === null || data === void 0 ? void 0 : (_data$listTableModels = data.listTableModels) === null || _data$listTableModels === void 0 ? void 0 : _data$listTableModels.map(function (path) {
          return {
            id: path.path,
            name: " ".concat(path.path, " }}")
          };
        });
      }).catch(function (error) {
        throw error;
      });
    };
    var localItems = ref([]);
    watchEffect(function () {
      mentionItems({
        query: localQuery.value
      }).then(function (items) {
        localItems.value = items; // Update the reactive property with fetched items
      }).catch(function (error) {
        console.error('Failed to fetch items:', error);
        localItems.value = []; // Reset or handle error state appropriately
      });
    });
    var handleValidateTemplate = /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var _result$data, _result$data$validate, _result$data2, _result$data2$validat, result, error, valid;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              validating.value = true;
              _context.prev = 1;
              _context.next = 4;
              return refetchValidation({
                templateText: currentText.value
              });
            case 4:
              result = _context.sent;
              error = result === null || result === void 0 ? void 0 : (_result$data = result.data) === null || _result$data === void 0 ? void 0 : (_result$data$validate = _result$data.validateTemplateText) === null || _result$data$validate === void 0 ? void 0 : _result$data$validate.error;
              valid = result === null || result === void 0 ? void 0 : (_result$data2 = result.data) === null || _result$data2 === void 0 ? void 0 : (_result$data2$validat = _result$data2.validateTemplateText) === null || _result$data2$validat === void 0 ? void 0 : _result$data2$validat.valid;
              if (!error && valid) {
                window.$nuxt.$emit('userMsg', {
                  color: 'green',
                  message: 'Template saved'
                });
                emit('input', currentText.value);
              } else {
                window.$nuxt.$emit('userMsg', {
                  color: 'red',
                  message: 'Template validation failed. Changes have not been saved.'
                });
              }
              emit('input', currentText.value);
            case 9:
              _context.prev = 9;
              validating.value = false;
              return _context.finish(9);
            case 12:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[1,, 9, 12]]);
      }));
      return function handleValidateTemplate() {
        return _ref6.apply(this, arguments);
      };
    }();
    watch(function () {
      var _validatedText$value;
      return (_validatedText$value = validatedText.value) === null || _validatedText$value === void 0 ? void 0 : _validatedText$value.error;
    }, function () {
      var _validatedText$value2;
      if ((_validatedText$value2 = validatedText.value) !== null && _validatedText$value2 !== void 0 && _validatedText$value2.error) {
        emit('toggle-disable-save', true);
      } else {
        emit('toggle-disable-save', false);
      }
    });
    var percentage = computed(function () {
      var _editor$value, _editor$value$storage, _editor$value$storage2;
      return Math.round(100 / limit.value * ((_editor$value = editor.value) === null || _editor$value === void 0 ? void 0 : (_editor$value$storage = _editor$value.storage) === null || _editor$value$storage === void 0 ? void 0 : (_editor$value$storage2 = _editor$value$storage.characterCount) === null || _editor$value$storage2 === void 0 ? void 0 : _editor$value$storage2.characters()));
    });
    var processEditorText = function processEditorText(html) {
      console.log('html', html);
      return html
      // Convert hard breaks and any existing <br> tags into real newlines so that
      // mid‑paragraph line breaks (Shift/Cmd+Enter) are preserved in the template
      // string that we validate and save.
      .replace(/<br\s*\/?>/g, '\n').replace(/<p><\/p>/g, '\n').replace(/<p>/g, '').replace(/<\/p>/g, '').replace(/<span class="mention">/g, '') // removes the border from the mention
      .replace(/<\/span>/g, '');
    };
    onMounted(function () {
      editor.value = new Editor({
        editable: !props.disabled,
        onCreate: function onCreate(_ref7) {
          var editor = _ref7.editor;
          var initialValue = (props.value || '').replace(/\n/g, '<br>');
          editor.commands.setContent(initialValue, false, {
            preserveWhitespace: 'full'
          });
          // Initialize currentText with the initial value
          currentText.value = processEditorText(editor.getHTML());
        },
        onUpdate: function onUpdate(_ref8) {
          var editor = _ref8.editor;
          // Triggered on each keystroke
          var html = editor.getHTML(); // editor.getText() removes line breaks
          currentText.value = processEditorText(html);
        },
        onBlur: function onBlur(_ref9) {
          var editor = _ref9.editor;
          emit('blur', editor.getText());
        },
        extensions: [Document, Paragraph, Text, HardBreak, Mention.configure({
          HTMLAttributes: {
            class: 'mention'
          },
          suggestion: {
            // https://tiptap.dev/docs/editor/api/utilities/suggestion
            allowSpaces: true,
            char: '{{',
            allowedPrefixes: [' ', '\n'],
            startOfLine: false,
            items: function items(query) {
              localQuery.value = query.query;
              return mentionItems(query);
            },
            render: function render() {
              var component;
              var popup;
              return {
                onStart: function onStart(props) {
                  component = new VueRenderer(MentionList, {
                    parent: _this,
                    propsData: props
                  });
                  if (!props.clientRect) {
                    return;
                  }
                  popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: function appendTo() {
                      return document.body;
                    },
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start'
                  });
                },
                onUpdate: function onUpdate(props) {
                  var _component;
                  (_component = component) === null || _component === void 0 ? void 0 : _component.updateProps(props);
                  if (!props.clientRect) {
                    return;
                  }
                  popup[0].setProps({
                    getReferenceClientRect: props.clientRect
                  });
                },
                onKeyDown: function onKeyDown(props) {
                  if (props.event.key === 'Escape') {
                    popup[0].hide();
                    return true;
                  }
                  return component.ref.onKeyDown(props);
                },
                onExit: function onExit() {
                  popup[0].destroy();
                  component.destroy();
                }
              };
            }
          }
        })],
        editorProps: {
          handleKeyDown: function handleKeyDown(_, event) {
            // Ensure Shift+Enter / Cmd+Enter / Ctrl+Enter always insert a hard line break
            // and therefore trigger an update + validation/render with preserved newlines.
            if (event.key === 'Enter' && (event.shiftKey || event.metaKey || event.ctrlKey)) {
              var _editor$value2;
              event.preventDefault();
              (_editor$value2 = editor.value) === null || _editor$value2 === void 0 ? void 0 : _editor$value2.commands.setHardBreak();
              return true;
            }
            return false;
          }
        }
      });
    });
    onBeforeUnmount(function () {
      editor.value.destroy();
    });
    return {
      __sfc: true,
      props: props,
      emit: emit,
      editor: editor,
      limit: limit,
      supportedObjectsByContext: supportedObjectsByContext,
      supportedObjects: supportedObjects,
      tooltip: tooltip,
      nuxt2Context: nuxt2Context,
      apolloClient: apolloClient,
      validateResult: validateResult,
      refetchValidation: refetchValidation,
      validatedText: validatedText,
      validatedProcessedText: validatedProcessedText,
      validatedError: validatedError,
      localQuery: localQuery,
      showItems: showItems,
      validating: validating,
      currentText: currentText,
      tableNames: tableNames,
      mentionItems: mentionItems,
      localItems: localItems,
      handleValidateTemplate: handleValidateTemplate,
      percentage: percentage,
      processEditorText: processEditorText,
      EditorContent: EditorContent,
      renderMarkdown: renderMarkdown,
      replaceBraces: replaceBraces
    };
  }
});